import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

const API_BASE_URL = '/gleif';

interface ApiError {
  error?: string;
  details?: Array<{ message: string }>;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    if (error.response?.data?.details?.length) {
      return error.response.data.details.map((d) => d.message).join(', ');
    }

    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    const status = error.response?.status;
    if (status === 400) {
      return 'Invalid request. Please double-check your input.';
    }
    if (status === 401 || status === 403) {
      return 'You are not authorized to perform this action.';
    }
    if (status === 404) {
      return 'The requested resource was not found.';
    }
    if (status === 500) {
      return 'GLEIF service returned an error. Please try again.';
    }
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Check your internet connection.';
    }
  }

  return 'An unexpected error occurred. Please try again.';
}

export interface GleifGoldenCopyMeta {
  publishDate?: string;
}

export interface GleifEntityAddress {
  language?: string | null;
  addressLines?: string[];
  addressNumber?: string | null;
  addressNumberWithinBuilding?: string | null;
  mailRouting?: string | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  postalCode?: string | null;
}

export interface GleifEvent {
  validationDocuments?: string;
  effectiveDate?: string;
  recordedDate?: string;
  type?: string;
  status?: string;
}

export interface GleifEntity {
  legalName?: {
    name: string;
    language?: string;
  };
  otherNames?: Array<{ name: string; language?: string }>;
  transliteratedOtherNames?: Array<{ name: string; language?: string }>;
  legalAddress?: GleifEntityAddress;
  headquartersAddress?: GleifEntityAddress;
  registeredAt?: {
    id?: string | null;
    other?: string | null;
  };
  registeredAs?: string | null;
  jurisdiction?: string | null;
  category?: string | null;
  legalForm?: {
    id?: string | null;
    other?: string | null;
  };
  associatedEntity?: {
    lei?: string | null;
    name?: string | null;
  };
  status?: string | null;
  expiration?: {
    date?: string | null;
    reason?: string | null;
  };
  successorEntity?: {
    lei?: string | null;
    name?: string | null;
  };
  successorEntities?: Array<{ lei?: string | null; name?: string | null }>;
  creationDate?: string | null;
  subCategory?: string | null;
  otherAddresses?: GleifEntityAddress[];
  eventGroups?: Array<{
    groupType?: string;
    events?: GleifEvent[];
  }>;
}

export interface GleifRegistration {
  initialRegistrationDate?: string;
  lastUpdateDate?: string;
  status?: string;
  nextRenewalDate?: string;
  managingLou?: string;
  corroborationLevel?: string;
  validatedAt?: {
    id?: string;
    other?: string | null;
  };
  validatedAs?: string | null;
  otherValidationAuthorities?: Array<{ id?: string; other?: string | null }>;
}

export interface GleifAttributes {
  lei: string;
  entity: GleifEntity;
  registration?: GleifRegistration;
  bic?: string[];
  mic?: string | null;
  ocid?: string | null;
  qcc?: string | null;
  spglobal?: string[];
  conformityFlag?: string | null;
}

export interface GleifRecordData {
  type: string;
  id: string;
  attributes: GleifAttributes;
  relationships?: Record<string, unknown>;
  links?: {
    self?: string;
  };
}

export interface GleifSearchResults {
  meta?: {
    goldenCopy?: GleifGoldenCopyMeta;
  };
  data?: GleifRecordData;
}

export interface GleifSearchResponse {
  results: GleifSearchResults | 'No results';
}

export interface GleifNameSearchResponse {
  results: GleifRecordData[];
}

export interface GleifExtractResponse {
  lei: string | null;
  confidence?: 'high' | 'medium' | 'low' | null;
  reason?: string | null;
}

interface ExtractPayload {
  content: string;
  title?: string;
}

export function useGleifSearch() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (lei: string) => {
      try {
        const { data } = await axios.post<GleifSearchResponse>(`${API_BASE_URL}/search`, {
          lei,
        });
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}

export function useGleifSearchByName() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (name: string) => {
      try {
        const { data } = await axios.post<GleifNameSearchResponse>(`${API_BASE_URL}/search-by-name`, {
          name,
        });
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}

export function useGleifExtractLei() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (payload: ExtractPayload) => {
      try {
        const { data } = await axios.post<GleifExtractResponse>(`${API_BASE_URL}/extract`, payload);
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}
